import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { Controller, FieldValues } from 'react-hook-form'
import { InputProps } from '../../../store/types'
import { t } from 'i18next'
import { useSelector } from 'react-redux'
import { getCurrentMode } from '../../../store/redux/reducers/darkModeSlice'
import { paletteDark, paletteLight } from '../../../styles/theme-configuration/palettes'

export type Option = {
  label: string
  value: string
}

interface SelectInputProps<T extends FieldValues> extends InputProps<T> {
  options: Option[]
  required?: boolean
}

const SelectInput = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  rules,
  required,
  disabled,
}: SelectInputProps<T>) => {
  const generateSingleOptions = () => {
    return options.length > 0 ? (
      options.map((option: any) => {
        return (
          <MenuItem key={option.value} value={option.value}>
            {t(option.label)}
          </MenuItem>
        )
      })
    ) : (
      <Typography sx={{ px: 2, py: '6px' }}>{t('noOptionsFound')}</Typography>
    )
  }
  const labelId = `${name}-label`
  const inputId = `${name}-input`
  const mode = useSelector(getCurrentMode)
  const palette = mode === 'light' ? paletteLight : paletteDark

  return (
    <FormControl required={required} size={'small'}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <InputLabel htmlFor={inputId} id={labelId} error={!!error}>
              {label}
            </InputLabel>
            <Select
              onChange={onChange}
              value={options.some(option => option.value === value) ? value : ''}
              label={label}
              required={required}
              error={!!error}
              disabled={disabled}
              inputProps={{
                'aria-labelledby': labelId,
                id: inputId,
              }}
              sx={{
                '&.Mui-disabled': {
                  '.MuiSelect-select': {
                    WebkitTextFillColor: palette.text.base,
                    color: palette.text.base,
                  },
                },
              }}
            >
              {generateSingleOptions()}
            </Select>
            {error && <FormHelperText error={true}>{error.message}</FormHelperText>}
          </>
        )}
      />
    </FormControl>
  )
}

export default SelectInput
